<script>
import { mapState, mapActions } from 'pinia'
import { useClientStore, useRouterStore, FormMixin } from '@artup/client'
import { Account } from '@artup/model'
import { useAccountsStore } from '../../../store'
import AccountsForm from '../../../components/accounts-form.vue'

export default {
  mixins: [
    FormMixin
  ],

  components: {
    'artup-accounts-view': AccountsForm
  },

  data () {
    return {
      // Indicates whether we're in the initial loading phase
      isInitialized: false,
      // Password
      password: '',
      // Password reset request details
      request: null,
      // Flag indicating whether password should be displayed in clear text
      revealPassword: false,
      // Indicates whether password reset has been attempted
      passwordResetAttempted: false,
      // Password reset error
      passwordResetError: null
    }
  },

  computed: {
    ...mapState(useRouterStore, [
      'currentRoute'
    ]),

    ...mapState(useClientStore, [
      'isDevelopmentEnvironment',
      'isMobilePhone',
      'configuration',
      'isBusy',
      'ticker'
    ]),

    // Account associated with the request
    account () {
      return this.request ? new Account(this.request.account) : null
    },

    // Indicates that password reset request has been retrieved and it is still valid
    hasValidRequest () {
      const { request } = this
      return request != null && !request.hasExpired
    },

    // Indicates that password reset request has been retrieved but it has expired
    requestExpired () {
      const { request } = this
      return request != null && request.hasExpired
    },

    // Indicates that password reset request has already been used
    requestUsed () {
      const { request } = this
      return request != null && request.hasBeenUsed
    },

    // Password reset token
    token () {
      return this.currentRoute.params.token
    },

    // Indicates that UI should be locked due to the ongoing action
    isLocked () {
      return this.isBusy
    },

    // Indicates whether the user has filled in all the required data
    // and we can proceed with password reset
    canResetPassword () {
      const { password, isLocked } = this
      return !isLocked && Boolean(password.trim())
    },

    // Indicates that password reset has failed
    passwordResetFailed () {
      return this.passwordResetAttempted && !this.account
    },

    // Indicates that password reset has succeeded
    passwordResetSucceeded () {
      return (this.passwordResetAttempted && this.account) || (this.request.hasBeenUsed)
    },

    // Dialog subtitle
    subtitle () {
      const { hasValidRequest, passwordResetSucceeded, requestUsed, requestExpired } = this
      if (passwordResetSucceeded) {
        return 'Your password has been reset'
      }
      if (hasValidRequest) {
        return 'Please enter the password'
      }
      if (requestUsed) {
        return 'Your password has been reset'
      }
      if (requestExpired) {
        return 'Password reset was not completed in time'
      }
      return 'Password reset failed'
    }
  },

  methods: {
    ...mapActions(useClientStore, [
      'busy',
      'done'
    ]),

    ...mapActions(useAccountsStore, [
      'getSecurityRequest',
      'changePassword',
    ]),

    async populate ({ token } = {}) {
      this.request = null
      if (token) {
        this.request = await this.getSecurityRequest({ token, geo: true })
      }
    },

    // Resets the account password
    async save (reCaptchaToken) {
      const { token, account: { name }, password } = this
      this.passwordResetAttempted = true

      try {
        await this.busy({ message: `Changing the password for ${name} ...` })
        await this.changePassword({ token, name, password, sendEmail: true, reCaptchaToken })
        await this.done()

      } catch (error) {
        this.passwordResetError = error.message

      } finally {
        await this.done()
      }
    }
  },

  async beforeRouteUpdate (to) {
    await this.populate({ token: to.params.token })
  },

  async created () {
    await this.populate({ token: this.token })
    this.isInitialized = true
  }
}

</script>

<template>
  <artup-accounts-view v-cloak v-if="isInitialized" class="activate" title="Change Password"
    :subtitle="subtitle">

    <template #content>
      <!-- Password reset request exists -->
      <q-form ref="form" class="form q-mt-lg" v-if="hasValidRequest && !passwordResetAttempted">
        <div class="fields">
          <div>
            <q-input class="input-name q-mt-sm" outlined :model-value="account.name"
              readonly maxlength="45">
            </q-input>
          </div>

          <div class="q-mt-sm">
            <q-input class="input-password q-mt-sm" outlined label="Password"
              v-model="password" maxlength="45"
              :type="revealPassword ? 'text' : 'password'">

              <template v-slot:append>
                <q-icon name="visibility" class="button-reveal-password cursor-pointer"
                  :disable="isLocked" @click="revealPassword = !revealPassword">
                </q-icon>
              </template>
            </q-input>
          </div>

        </div>

        <div class="buttons q-mt-lg">
          <q-btn class="button-save bg-grey-9 text-white q-pb-md q-pt-md" label="Change Password"
            unelevated rounded no-caps @click="save()" :disable="!canResetPassword">
          </q-btn>
        </div>
      </q-form>

      <!-- Password reset succeeded -->
      <div class="form q-mt-sm" v-else-if="passwordResetSucceeded">
        <div class="fields text-center">
          Your password has been changed.
          <br>
          You can now log in to ARTup community!
        </div>
        <div class="buttons q-mt-lg q-pl-xl q-pr-xl">
          <q-btn class="button-save bg-grey-9 text-white q-pb-md q-pt-md" label="Continue to login"
            unelevated rounded no-caps :to="{ name: 'login', query: { name: account.name } }">
          </q-btn>
        </div>
      </div>

      <!-- Password reset failed -->
      <div class="form q-mt-sm" v-else-if="hasValidRequest && passwordResetFailed">
        <div class="fields text-center">
          We apologize for the inconvenience.
          <br>
          Please try again at later time.
        </div>
        <div class="buttons q-mt-lg q-pl-xl q-pr-xl">
          <q-btn class="button-save bg-grey-9 text-white q-pb-md q-pt-md"
            label="Try another sign up" unelevated rounded no-caps :to="{ name: 'signup' }">
          </q-btn>
        </div>
      </div>

      <!-- Password reset request not found -->
      <div class="form q-mt-sm" v-else>
        <div class="fields column items-center">
          <div class="column items-center justify-center text-orange-9">
            <label>
              Your password request has already expired.
            </label>
            <label>
              Click the button below to try again.
            </label>
          </div>
        </div>

        <div class="buttons q-mt-lg q-pl-xl q-pr-xl">
          <q-btn class="button-save bg-grey-9 text-white q-pb-md q-pt-md" label="Sign Up" unelevated
            rounded no-caps :to="{ name: 'login' }">
          </q-btn>
        </div>
      </div>
    </template>

  </artup-accounts-view>
</template>

<style scoped lang="scss"></style>